import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Package, Search, RefreshCw, Plus, Minus, Loader2, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChemistInventoryTab({ token: propToken, vendorType }) {
  const getHeaders = () => ({ Authorization: `Bearer ${propToken || localStorage.getItem('vendor_token')}` });
  const itemLabel = vendorType === 'lab' ? 'test' : vendorType === 'inventory' || vendorType === 'marketing' ? 'product' : 'medicine';

  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [adjustDialog, setAdjustDialog] = useState({ open: false, med: null });
  const [adjustQty, setAdjustQty] = useState(0);
  const [adjustReason, setAdjustReason] = useState('');
  const [fetching, setFetching] = useState(false);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/chemist/inventory?search=${search}`, { headers: getHeaders() });
      setMedicines(res.data);
    } catch { toast.error('Failed to load inventory'); }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  const adjustStock = async () => {
    if (!adjustQty) { toast.error('Enter quantity'); return; }
    try {
      await axios.put(`${API}/api/chemist/inventory/${adjustDialog.med.id}/stock`, {
        medicine_id: adjustDialog.med.id,
        adjustment: adjustQty,
        reason: adjustReason
      }, { headers: getHeaders() });
      toast.success('Stock updated');
      setAdjustDialog({ open: false, med: null });
      setAdjustQty(0); setAdjustReason('');
      fetchInventory();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update stock'); }
  };

  const autoFetch = async () => {
    setFetching(true);
    try {
      const res = await axios.post(`${API}/api/chemist/inventory/auto-fetch`, {}, { headers: getHeaders() });
      toast.success(res.data.message);
      fetchInventory();
    } catch { toast.error('Failed to auto-fetch'); }
    setFetching(false);
  };

  const lowStock = medicines.filter(m => (m.stock_quantity || 0) <= 10);

  return (
    <div className="space-y-4" data-testid="chemist-inventory-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <Input placeholder={`Search ${itemLabel}s or barcode...`} value={search} onChange={(e) => setSearch(e.target.value)} className="w-56 h-9" />
          <Button size="sm" variant="outline" onClick={fetchInventory}><Search className="w-4 h-4" /></Button>
        </div>
        <div className="flex gap-2">
          {vendorType === 'chemist' && (
            <Button size="sm" variant="outline" onClick={autoFetch} disabled={fetching} data-testid="auto-fetch-btn">
              {fetching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Auto-Fetch from Purchases
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={fetchInventory}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-3">
            <p className="text-sm font-medium text-amber-700">Low Stock Alert: {lowStock.length} items with stock ≤ 10</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {lowStock.slice(0, 5).map(m => (
                <Badge key={m.id} variant="outline" className="text-amber-700 border-amber-300 text-xs">
                  {m.name}: {m.stock_quantity || 0}
                </Badge>
              ))}
              {lowStock.length > 5 && <Badge variant="outline" className="text-amber-700 border-amber-300 text-xs">+{lowStock.length - 5} more</Badge>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inventory Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Package className="w-4 h-4" /> Inventory ({medicines.length} items)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-slate-50">
                  <th className="text-left p-2">Medicine</th>
                  <th className="text-left p-2">Category</th>
                  <th className="text-right p-2">MRP</th>
                  <th className="text-right p-2">B2B Price</th>
                  <th className="text-right p-2">Stock</th>
                  <th className="p-2">Actions</th>
                </tr></thead>
                <tbody>
                  {medicines.map(med => (
                    <tr key={med.id} className="border-b hover:bg-slate-50">
                      <td className="p-2">
                        <p className="font-medium">{med.name}</p>
                        <p className="text-xs text-slate-500">{med.generic_name} • {med.pack_size} {med.barcode && `• #${med.barcode}`}</p>
                      </td>
                      <td className="p-2"><Badge variant="secondary" className="text-xs">{med.category}</Badge></td>
                      <td className="p-2 text-right">₹{med.mrp}</td>
                      <td className="p-2 text-right font-medium">₹{med.b2b_price}</td>
                      <td className="p-2 text-right">
                        <Badge className={`${(med.stock_quantity || 0) <= 10 ? 'bg-red-100 text-red-700' : (med.stock_quantity || 0) <= 50 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                          {med.stock_quantity || 0}
                        </Badge>
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setAdjustDialog({ open: true, med }); setAdjustQty(0); }}>
                            <Plus className="w-3 h-3 mr-1" /> / <Minus className="w-3 h-3 ml-1" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {medicines.length === 0 && <p className="text-center text-slate-400 py-6">No inventory items. Add medicines from your catalog or use Auto-Fetch.</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stock Adjustment Dialog */}
      <Dialog open={adjustDialog.open} onOpenChange={(o) => !o && setAdjustDialog({ open: false, med: null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust Stock: {adjustDialog.med?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Current Stock: <strong>{adjustDialog.med?.stock_quantity || 0}</strong></p>
            <div>
              <label className="text-xs text-slate-500">Adjustment (+ to add, - to deduct)</label>
              <Input type="number" value={adjustQty} onChange={(e) => setAdjustQty(parseInt(e.target.value) || 0)} placeholder="e.g. +50 or -10" data-testid="stock-adjust-qty" />
            </div>
            <Input placeholder="Reason (optional)" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
            <p className="text-sm">New Stock: <strong>{(adjustDialog.med?.stock_quantity || 0) + adjustQty}</strong></p>
            <Button className="w-full" onClick={adjustStock}>Update Stock</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
